<template>
  <div class="health-score-page">
    <topsearch title="Müşteri Sıcaklık Raporu" />

    <!-- İstatistik Kartları -->
    <div class="stats-container" v-if="stats">
      <div class="stat-card healthy" @click="filterByCategory('healthy')">
        <div class="stat-indicator"></div>
        <div class="stat-content">
          <h3>Sağlıklı</h3>
          <p class="stat-count">{{ stats.healthy_count || 0 }}</p>
          <p class="stat-percentage">{{ healthyPercentage }}%</p>
        </div>
      </div>

      <div class="stat-card risky" @click="filterByCategory('risky')">
        <div class="stat-indicator"></div>
        <div class="stat-content">
          <h3>Riskli</h3>
          <p class="stat-count">{{ stats.risky_count || 0 }}</p>
          <p class="stat-percentage">{{ riskyPercentage }}%</p>
        </div>
      </div>

      <div class="stat-card critical" @click="filterByCategory('critical')">
        <div class="stat-indicator"></div>
        <div class="stat-content">
          <h3>Kritik</h3>
          <p class="stat-count">{{ stats.critical_count || 0 }}</p>
          <p class="stat-percentage">{{ criticalPercentage }}%</p>
        </div>
      </div>

      <div class="stat-card churn" @click="filterByCategory('churn')">
        <div class="stat-indicator"></div>
        <div class="stat-content">
          <h3>Kayıp Riski</h3>
          <p class="stat-count">{{ stats.churn_count || 0 }}</p>
          <p class="stat-percentage">{{ churnPercentage }}%</p>
        </div>
      </div>

      <div class="stat-card total">
        <div class="stat-indicator"></div>
        <div class="stat-content">
          <h3>Toplam Müşteri</h3>
          <p class="stat-count">{{ stats.total_customers || 0 }}</p>
          <p class="stat-avg">Ort. Skor: {{ stats.avg_health_score || 0 }}</p>
        </div>
      </div>
    </div>

    <!-- Filtreler -->
    <div class="filters-container">
      <div class="filter-group">
        <label>Kategori:</label>
        <select v-model="selectedCategory" @change="fetchData">
          <option value="">Tümü</option>
          <option value="healthy">Sağlıklı (80-100)</option>
          <option value="risky">Riskli (50-79)</option>
          <option value="critical">Kritik (30-49)</option>
          <option value="churn">Kayıp Riski (0-29)</option>
        </select>
      </div>

      <div class="filter-group">
        <label>Sıralama:</label>
        <select v-model="sortBy" @change="fetchData">
          <option value="health_score">Health Score</option>
          <option value="name">İsim</option>
          <option value="last_login">Son Login</option>
          <option value="total_deposits">Toplam Yatırım</option>
        </select>
      </div>

      <div class="filter-group">
        <label>Sıra:</label>
        <select v-model="sortOrder" @change="fetchData">
          <option value="ASC">Artan</option>
          <option value="DESC">Azalan</option>
        </select>
      </div>

      <div class="filter-group">
        <label>Min. Yatırım (TL):</label>
        <input
          type="number"
          v-model.number="minDeposit"
          @change="fetchData"
          min="0"
          step="100"
          placeholder="Minimum yatırım"
        />
      </div>

      <div class="filter-group">
        <label>Müşteri Ara:</label>
        <input
          type="text"
          v-model="searchQuery"
          @input="onSearchChange"
          placeholder="İsim veya telefon..."
        />
      </div>

      <button class="btn-refresh" @click="fetchData" :disabled="loading">
        <span v-if="!loading">Yenile</span>
        <span v-else>Yükleniyor...</span>
      </button>

      <div class="info-tooltip">
        Sıcaklık skoru müşterinin aktivite durumunu 0-100 arası gösterir
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading && customers.length === 0" class="loading-state">
      <div class="spinner"></div>
      <p>Sıcaklık skoru hesaplanıyor...</p>
    </div>

    <!-- Müşteri Listesi -->
    <div v-else-if="customers.length > 0" class="customers-table-container">
      <!-- Tablo Filtreleri -->
      <div class="table-filters" v-if="hasTableFilters || tableFilters.retention">
        <input
          type="text"
          v-model="tableFilters.retention"
          placeholder="Retention Ara..."
          class="table-filter-input"
        />
        <button @click="clearTableFilters" class="btn-clear-filters" v-if="hasTableFilters">
          Temizle
        </button>
      </div>

      <table class="health-score-table">
        <thead>
          <tr>
            <th @click="sortTable('name')" class="sortable">
              Müşteri <span class="sort-icon">{{ getSortIcon('name') }}</span>
            </th>
            <th @click="sortTable('health_score')" class="sortable">
              Sıcaklık Skoru <span class="sort-icon">{{ getSortIcon('health_score') }}</span>
            </th>
            <th>Durum</th>
            <th>Retention</th>
            <th @click="sortTable('last_login')" class="sortable">
              {{ loginColumnLabel || 'Son Giriş' }} <span class="sort-icon">{{ getSortIcon('last_login') }}</span>
            </th>
            <th>Son İletişim</th>
            <th @click="sortTable('total_deposits')" class="sortable">
              Toplam Yatırım <span class="sort-icon">{{ getSortIcon('total_deposits') }}</span>
            </th>
            <th>Toplam Çekim</th>
            <th>İşlemler</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="customer in filteredCustomers"
            :key="customer.id"
            :class="getRowClass(customer.health_score)"
          >
            <td>
              <router-link :to="`/leadDetail/${customer.id}`" class="customer-name">
                {{ customer.name }}
              </router-link>
            </td>
            <td class="score-cell">
              <div
                class="score-badge-small"
                :class="getScoreClass(customer.health_score)"
              >
                {{ customer.health_score }}
              </div>
            </td>
            <td>
              <span class="category-badge" :class="getCategoryBadgeClass(customer.health_score)">
                {{ formatCategoryText(customer.health_score) }}
              </span>
            </td>
            <td>{{ customer.retention_name || "-" }}</td>
            <td :class="getDaysClass(customer.days_since_login)">
              {{ formatDaysAgo(customer.days_since_login) }}
            </td>
            <td :class="getDaysClass(customer.days_since_contact)">
              {{ formatDaysAgo(customer.days_since_contact) }}
            </td>
            <td class="amount">{{ formatCurrency(customer.total_deposits) }}</td>
            <td class="amount">{{ formatCurrency(customer.total_withdrawals) }}</td>
            <td class="actions">
              <button
                class="btn-detail"
                @click="viewDetail(customer.id)"
                title="Detay Görüntüle"
              >
                Detay
              </button>
              <button
                class="btn-call"
                @click="callCustomer(customer.id)"
                title="Müşteri Sayfası"
              >
                Görüntüle
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="pagination" v-if="customers.length > 0">
        <button @click="previousPage" :disabled="currentPage === 0">
          ← Önceki
        </button>
        <span>Sayfa {{ currentPage + 1 }} ({{ filteredCustomers.length }} / {{ customers.length }} kayıt)</span>
        <button @click="nextPage" :disabled="customers.length < limit">
          Sonraki →
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <p>Gösterilecek müşteri bulunamadı.</p>
      <button @click="clearFilters" class="btn-clear">Filtreleri Temizle</button>
    </div>

    <!-- Detail Modal -->
    <HealthScoreDetailModal
      v-if="showDetailModal"
      :customer-id="selectedCustomerId"
      @close="showDetailModal = false"
    />
  </div>
</template>

<script>
import http from "../services/http";
import HealthScoreDetailModal from "../components/HealthScoreDetailModal.vue";

export default {
  name: "HealthScore",
  components: {
    HealthScoreDetailModal,
  },
  data() {
    return {
      customers: [],
      stats: null,
      loading: false,
      selectedCategory: "",
      minDeposit: 100,
      sortBy: "health_score",
      sortOrder: "ASC",
      limit: 50,
      currentPage: 0,
      showDetailModal: false,
      selectedCustomerId: null,
      tableFilters: {
        retention: "",
      },
      loginColumnLabel: "",
      searchQuery: "",
      searchTimeout: null,
    };
  },
  computed: {
    healthyPercentage() {
      if (!this.stats || !this.stats.total_customers) return 0;
      return ((this.stats.healthy_count / this.stats.total_customers) * 100).toFixed(1);
    },
    riskyPercentage() {
      if (!this.stats || !this.stats.total_customers) return 0;
      return ((this.stats.risky_count / this.stats.total_customers) * 100).toFixed(1);
    },
    criticalPercentage() {
      if (!this.stats || !this.stats.total_customers) return 0;
      return ((this.stats.critical_count / this.stats.total_customers) * 100).toFixed(1);
    },
    churnPercentage() {
      if (!this.stats || !this.stats.total_customers) return 0;
      return ((this.stats.churn_count / this.stats.total_customers) * 100).toFixed(1);
    },
    filteredCustomers() {
      return this.customers.filter(customer => {
        const retentionMatch = !this.tableFilters.retention ||
          (customer.retention_name && customer.retention_name.toLowerCase().includes(this.tableFilters.retention.toLowerCase()));
        return retentionMatch;
      });
    },
    hasTableFilters() {
      return this.tableFilters.retention;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const response = await http.getHealthScoreReport({
          loginUserId: this.$root.cache.userId,
          loginUserDepartment: this.$root.cache.department,
          userGroupId: this.$root.cache.userGroupId,
          health_category: this.selectedCategory || undefined,
          min_deposit: this.minDeposit || 0,
          search: this.searchQuery || undefined,
          sort_by: this.sortBy,
          sort_order: this.sortOrder,
          limit: this.limit,
          offset: this.currentPage * this.limit,
        });

        if (response.data.success) {
          this.customers = response.data.data;
          this.stats = response.data.stats;
          this.loginColumnLabel = response.data.login_source === 'mt5' ? 'MT5 Son Giriş' : 'Son Aktivite';
        } else {
          console.error("Rapor hatası:", response.data.message || "Rapor yüklenemedi");
          alert(response.data.message || "Rapor yüklenemedi");
        }
      } catch (error) {
        console.error("Sıcaklık raporu hatası:", error);
        const errorMessage = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message;
        alert("Rapor yüklenirken bir hata oluştu: " + errorMessage);
      } finally {
        this.loading = false;
      }
    },

    filterByCategory(category) {
      this.selectedCategory = category;
      this.currentPage = 0;
      this.fetchData();
    },

    clearFilters() {
      this.selectedCategory = "";
      this.minDeposit = 100;
      this.searchQuery = "";
      this.sortBy = "health_score";
      this.sortOrder = "ASC";
      this.currentPage = 0;
      this.fetchData();
    },

    onSearchChange() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 0;
        this.fetchData();
      }, 500);
    },

    nextPage() {
      this.currentPage++;
      this.fetchData();
    },

    previousPage() {
      if (this.currentPage > 0) {
        this.currentPage--;
        this.fetchData();
      }
    },

    getScoreClass(score) {
      if (score >= 80) return "healthy";
      if (score >= 50) return "risky";
      if (score >= 30) return "critical";
      return "churn";
    },

    getRowClass(score) {
      return `row-${this.getScoreClass(score)}`;
    },

    getDaysClass(days) {
      if (days < 7) return "recent";
      if (days < 30) return "moderate";
      return "old";
    },

    formatDaysAgo(days) {
      if (!days || days > 999) return "Hiç";
      if (days === 0) return "Bugün";
      if (days === 1) return "Dün";
      if (days < 30) return `${days} gün önce`;
      const months = Math.floor(days / 30);
      return `${months} ay önce`;
    },

    formatCurrency(amount) {
      if (!amount) return "0 TL";
      return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
      }).format(amount);
    },

    viewDetail(customerId) {
      this.selectedCustomerId = customerId;
      this.showDetailModal = true;
    },

    callCustomer(customerId) {
      this.$router.push(`/leadDetail/${customerId}`);
    },

    formatCategoryText(score) {
      if (score >= 80) return "Sağlıklı";
      if (score >= 50) return "Riskli";
      if (score >= 30) return "Kritik";
      return "Kayıp Riski";
    },

    getCategoryBadgeClass(score) {
      if (score >= 80) return "badge-healthy";
      if (score >= 50) return "badge-risky";
      if (score >= 30) return "badge-critical";
      return "badge-churn";
    },

    sortTable(field) {
      if (this.sortBy === field) {
        // Aynı kolona tekrar tıklanırsa sıralama yönünü değiştir
        this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
      } else {
        // Farklı kolona tıklanırsa o kolonu seç ve ASC yap
        this.sortBy = field;
        this.sortOrder = 'ASC';
      }
      this.currentPage = 0;
      this.fetchData();
    },

    getSortIcon(field) {
      if (this.sortBy !== field) return '↕';
      return this.sortOrder === 'ASC' ? '↑' : '↓';
    },

    clearTableFilters() {
      this.tableFilters.retention = "";
    },
  },
};
</script>

<style scoped>
.health-score-page {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

/* İstatistik Kartları */
.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
  margin-top: 4px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.stat-card.healthy {
  border-left: 4px solid #4caf50;
}
.stat-card.risky {
  border-left: 4px solid #ff9800;
}
.stat-card.critical {
  border-left: 4px solid #ff5722;
}
.stat-card.churn {
  border-left: 4px solid #f44336;
}
.stat-card.total {
  border-left: 4px solid #2196f3;
}

.stat-indicator {
  width: 6px;
  height: 45px;
  border-radius: 3px;
  flex-shrink: 0;
}

.stat-card.healthy .stat-indicator {
  background: #4caf50;
}

.stat-card.risky .stat-indicator {
  background: #ff9800;
}

.stat-card.critical .stat-indicator {
  background: #ff5722;
}

.stat-card.churn .stat-indicator {
  background: #f44336;
}

.stat-card.total .stat-indicator {
  background: #2196f3;
}

.stat-content h3 {
  margin: 0 0 4px 0;
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.stat-count {
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.stat-percentage,
.stat-avg {
  margin: 3px 0 0 0;
  font-size: 12px;
  color: #999;
}

/* Filtreler */
.filters-container {
  background: white;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  display: flex;
  gap: 20px;
  align-items: end;
  flex-wrap: wrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-group label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.filter-group select,
.filter-group input[type="number"],
.filter-group input[type="text"] {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 180px;
}

.filter-group input:focus {
  outline: none;
  border-color: #2196f3;
}

.btn-refresh {
  padding: 8px 20px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.3s;
}

.btn-refresh:hover:not(:disabled) {
  background: #1976d2;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.info-tooltip {
  font-size: 13px;
  color: #666;
  padding: 8px 16px;
  background: #f5f7fa;
  border-radius: 6px;
  border-left: 3px solid #2196f3;
}

/* Tablo */
.customers-table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.table-filters {
  padding: 15px 20px;
  background: #f9f9f9;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.table-filter-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  min-width: 200px;
  transition: border-color 0.2s;
}

.table-filter-input:focus {
  outline: none;
  border-color: #2196f3;
}

.btn-clear-filters {
  padding: 8px 16px;
  background: #ff5722;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.3s;
}

.btn-clear-filters:hover {
  background: #e64a19;
}

.health-score-table {
  width: 100%;
  border-collapse: collapse;
}

.health-score-table thead {
  background: #f5f7fa;
}

.health-score-table th {
  padding: 15px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #666;
  border-bottom: 2px solid #e0e0e0;
}

.health-score-table th.sortable {
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
}

.health-score-table th.sortable:hover {
  background: #e8f0fe;
}

.health-score-table .sort-icon {
  font-size: 12px;
  color: #999;
  margin-left: 4px;
}

.health-score-table td {
  padding: 12px;
  font-size: 13px;
  border-bottom: 1px solid #f0f0f0;
}

.health-score-table tbody tr:hover {
  background: #f9f9f9;
}

/* Score Badge */
.score-cell {
  text-align: center;
}

.score-badge-small {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-weight: bold;
  font-size: 14px;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.score-badge-small.healthy {
  background: #4caf50;
}
.score-badge-small.risky {
  background: #ff9800;
}
.score-badge-small.critical {
  background: #ff5722;
}
.score-badge-small.churn {
  background: #f44336;
}

/* Category Badge */
.category-badge {
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 12px;
  color: white;
  display: inline-block;
  white-space: nowrap;
}

.badge-healthy {
  background: #4caf50;
}
.badge-risky {
  background: #ff9800;
}
.badge-critical {
  background: #ff5722;
}
.badge-churn {
  background: #f44336;
}

/* Days Classes */
.recent {
  color: #4caf50;
  font-weight: 500;
}
.moderate {
  color: #ff9800;
}
.old {
  color: #f44336;
}

.amount {
  font-weight: 500;
  color: #333;
}

/* Actions */
.actions {
  display: flex;
  gap: 8px;
}

.actions button {
  padding: 6px 14px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  color: #333;
}

.btn-detail {
  border-color: #2196f3;
  color: #2196f3;
}

.btn-call {
  border-color: #4caf50;
  color: #4caf50;
}

.btn-detail:hover {
  background: #2196f3;
  color: white;
}

.btn-call:hover {
  background: #4caf50;
  color: white;
}

/* Customer Name */
.customer-name {
  color: #2196f3;
  text-decoration: none;
  font-weight: 500;
}

.customer-name:hover {
  text-decoration: underline;
}

/* Pagination */
.pagination {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  border-top: 1px solid #e0e0e0;
}

.pagination button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.pagination button:hover:not(:disabled) {
  background: #f5f7fa;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #2196f3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
}

.empty-state p {
  font-size: 18px;
  color: #666;
  margin-bottom: 20px;
}

.btn-clear {
  padding: 10px 24px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-clear:hover {
  background: #1976d2;
}
</style>
